// Evaluates the parsed lines in order, each seeing the variables assigned
// above it. Walks the tree rather than compiling it to javascript as CalcPad
// does, so a name in a note never reaches a global: only variables, `PI`,
// `E` and Math's functions are known.
import {
  type BinaryOperator,
  type Expr,
  type Line,
  type Span,
} from './ast';
import { parseProgram } from './parser';

export type LineResult =
  | { readonly kind: 'empty' }
  | { readonly kind: 'value', readonly value: number }
  | { readonly kind: 'error', readonly message: string, readonly span: Span };

type Scope = ReadonlyMap<string, number>;

// comparisons and `!` give booleans, which only a condition or `&&` takes
type Value = number | boolean;

const mathFunctions: {
  readonly [name: string]: (...args: number[]) => number,
} = {
  abs: Math.abs,
  acos: Math.acos,
  asin: Math.asin,
  atan: Math.atan,
  atan2: Math.atan2,
  ceil: Math.ceil,
  cos: Math.cos,
  exp: Math.exp,
  floor: Math.floor,
  log: Math.log,
  max: Math.max,
  min: Math.min,
  pow: Math.pow,
  random: Math.random,
  round: Math.round,
  sin: Math.sin,
  sqrt: Math.sqrt,
  tan: Math.tan,
};

// case-insensitive: `pi` and `Pi` are `PI`
const constants: { readonly [name: string]: number } = {
  PI: Math.PI,
  E: Math.E,
};

const hasOwn = (o: object, key: string) =>
  Object.prototype.hasOwnProperty.call(o, key);

const notDefined = (name: string) =>
  new ReferenceError(`${name} is not defined`);

// javascript's own semantics, as CalcPad's compiled code has them
const operators: {
  readonly [operator in Exclude<BinaryOperator, '&&' | '||'>]: (
    a: any,
    b: any,
  ) => Value;
} = {
  '+': (a, b) => a + b,
  '-': (a, b) => a - b,
  '*': (a, b) => a * b,
  '/': (a, b) => a / b,
  '^': (a, b) => a ** b,
  'mod': (a, b) => a % b,
  '<': (a, b) => a < b,
  '<=': (a, b) => a <= b,
  '>': (a, b) => a > b,
  '>=': (a, b) => a >= b,
  '==': (a, b) => a === b,
  '!=': (a, b) => a !== b,
};

const percentages = {
  of: (percentage: number, base: number) => base * percentage / 100,
  on: (percentage: number, base: number) => base * percentage / 100 + base,
  off: (percentage: number, base: number) => base - base * percentage / 100,
};

const lookup = (scope: Scope, name: string): number => {
  const value = scope.get(name);
  if (value !== undefined) return value;
  const constant = name.toUpperCase();
  if (hasOwn(constants, constant)) return constants[constant];
  throw notDefined(name);
};

const call = (scope: Scope, name: string, args: number[]): number => {
  if (scope.has(name)) throw new TypeError(`${name} is not a function`);
  if (!hasOwn(mathFunctions, name)) throw notDefined(name);
  return mathFunctions[name](...args);
};

function evaluateExpr(expr: Expr, scope: Scope): Value {
  const recur = (e: Expr): any => evaluateExpr(e, scope);

  switch (expr.kind) {
    case 'number':
      return expr.value;
    case 'identifier':
      return lookup(scope, expr.name);
    case 'unary':
      return expr.operator === '-'
        ? -recur(expr.operand)
        : expr.operator === '+'
        ? +recur(expr.operand)
        : !recur(expr.operand);
    case 'binary':
      // short-circuiting, as in javascript
      return expr.operator === '&&'
        ? recur(expr.left) && recur(expr.right)
        : expr.operator === '||'
        ? recur(expr.left) || recur(expr.right)
        : operators[expr.operator](recur(expr.left), recur(expr.right));
    case 'percentage':
      return percentages[expr.operator](
        recur(expr.percentage),
        recur(expr.base),
      );
    case 'conditional':
      return recur(expr.test) ? recur(expr.consequent) : recur(expr.alternate);
    case 'call':
      return call(scope, expr.callee, expr.args.map(recur));
  }
}

function evaluateLine(line: Line, scope: Scope): [LineResult, Scope] {
  switch (line.kind) {
    case 'empty':
      return [{ kind: 'empty' }, scope];
    case 'error':
      return [{ kind: 'error', message: line.message, span: line.span }, scope];
    case 'expression':
    case 'assignment':
      try {
        const value = evaluateExpr(line.expression, scope);
        if (typeof value !== 'number') {
          throw new TypeError(`Expected a number, got ${typeof value}`);
        }
        return [
          { kind: 'value', value },
          line.kind === 'assignment'
            ? new Map(scope).set(line.name, value)
            : scope,
        ];
      } catch (e) {
        return [{
          kind: 'error',
          message: e instanceof Error ? e.message : String(e),
          span: line.kind === 'assignment' ? line.span : line.expression.span,
        }, scope];
      }
  }
}

/**
 * Receives
 * 1 + 2
 * a = 20
 * a * 2
 *
 * and returns one result per line
 * [3, 20, 40]
 */
export const evaluate = (text: string): readonly LineResult[] =>
  parseProgram(text).reduce<[LineResult[], Scope]>(
    ([results, scope], line) => {
      const [result, next] = evaluateLine(line, scope);
      return [[...results, result], next];
    },
    [[], new Map()],
  )[0];
